import { useCallback, useMemo } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { Map as ImmutableMap } from 'immutable';
import UITruncateString from 'UIComponents/text/UITruncateString';
import * as EngagementTypes from 'customer-data-objects/engagement/EngagementTypes';
import UICheckbox from 'UIComponents/input/UICheckbox';
import Small from 'UIComponents/elements/Small';
import styled from 'styled-components';
import { NOTE, TASK, SCHEDULE, LOGGED_CALL, LOGGED_EMAIL, LOGGED_MEETING, LOGGED_LINKED_IN_MESSAGE, LOGGED_POSTAL_MAIL, LOGGED_SMS, LOGGED_WHATS_APP } from 'customer-data-objects/interaction/InteractionTypes';
import { CONTACT_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { SubstringHighlighter } from '../helpers/SubstringHighlighter';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const PLURAL_ENGAGEMENT_AND_INTERACTION_TYPE_KEYS = ImmutableMap({
  [EngagementTypes.CALL]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.call',
  [EngagementTypes.CONVERSATION_SESSION]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.conversation_session',
  default: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.default',
  [EngagementTypes.EMAIL]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.email',
  [EngagementTypes.EMAIL_ACTIVITY]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.email_activity',
  [EngagementTypes.FORWARDED_EMAIL]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.forwarded_email',
  [EngagementTypes.INCOMING_EMAIL]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.incoming_email',
  [LOGGED_CALL]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.logged-call',
  [LOGGED_EMAIL]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.logged-email',
  [LOGGED_LINKED_IN_MESSAGE]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.logged-linked-in-message',
  [LOGGED_MEETING]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.logged-meeting',
  [LOGGED_POSTAL_MAIL]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.logged-postal-mail',
  [LOGGED_SMS]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.logged-sms',
  [LOGGED_WHATS_APP]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.logged-whats-app',
  [SCHEDULE]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.schedule',
  [EngagementTypes.MEETING]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.meeting',
  [EngagementTypes.NOTE]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.note',
  [EngagementTypes.TASK]: 'universalAssociationsSelect.engagementAndInteractionTypesPlural.task'
});
const PERMISSIONLESS_ACTIVITIES = [LOGGED_CALL, LOGGED_EMAIL, LOGGED_LINKED_IN_MESSAGE, LOGGED_POSTAL_MAIL, LOGGED_MEETING, LOGGED_SMS, LOGGED_WHATS_APP, NOTE, TASK, EngagementTypes.NOTE, EngagementTypes.TASK];
const DEFAULT_VALUE = PLURAL_ENGAGEMENT_AND_INTERACTION_TYPE_KEYS.get('default');
const StyledUITruncateString = styled(UITruncateString).withConfig({
  displayName: "UniversalAssociationsSelectOption__StyledUITruncateString",
  componentId: "mnucj0-0"
})(["&&{font-size:14px;}"]);
const UniversalAssociationsSelectOption = ({
  associationOptionRecord,
  canEdit,
  emailRecipientsIds,
  engagementOrInteractionType,
  onToggleSelectAssociation,
  readOnly,
  searchText,
  shouldDisableLastAssociation,
  toObjectTypeId
}) => {
  const type = PLURAL_ENGAGEMENT_AND_INTERACTION_TYPE_KEYS.get(engagementOrInteractionType, DEFAULT_VALUE);
  const isPermissionlessActivity = PERMISSIONLESS_ACTIVITIES.includes(engagementOrInteractionType);
  const shouldAllowCommunication = isPermissionlessActivity || associationOptionRecord.get('currentUserCanCommunicate');
  const objectId = associationOptionRecord.get('objectId');
  const isEmailRecipient = Boolean(toObjectTypeId === CONTACT_TYPE_ID && emailRecipientsIds && emailRecipientsIds.includes(objectId));
  const isReadOnly = readOnly || !canEdit || isEmailRecipient || !shouldAllowCommunication || associationOptionRecord.get('isSelected') && shouldDisableLastAssociation;
  const title = useMemo(() => {
    if (readOnly) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "universalAssociationsSelect.readOnly"
      });
    }
    if (!canEdit) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "universalAssociationsSelect.tooltipEditPermissionError"
      });
    }
    if (!shouldAllowCommunication) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "universalAssociationsSelect.tooltipCommunicationPermissionErrorV2"
      });
    }
    if (isEmailRecipient) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "universalAssociationsSelect.disableEmailOption"
      });
    }
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "universalAssociationsSelect.lastAssociationToolTip",
      options: {
        engagementOrInteractionType: I18n.text(type)
      }
    });
  }, [canEdit, isEmailRecipient, readOnly, shouldAllowCommunication, type]);
  const handleToggleSelectAssociation = useCallback(() => {
    onToggleSelectAssociation(associationOptionRecord);
  }, [associationOptionRecord, onToggleSelectAssociation]);
  const Option = useMemo(() => {
    const primaryDisplayLabel = associationOptionRecord.get('primaryDisplayLabel');
    const secondaryDisplayLabel = associationOptionRecord.get('secondaryDisplayLabel');
    const tooltip = secondaryDisplayLabel ? `${primaryDisplayLabel} ${secondaryDisplayLabel}` : `${primaryDisplayLabel}`;
    return /*#__PURE__*/_jsx(UICheckbox, {
      alignment: "center",
      className: "m-bottom-2",
      checked: associationOptionRecord.get('isSelected'),
      "data-selenium-test": "association-option-checkbox",
      "data-selenium-id": objectId,
      onChange: handleToggleSelectAssociation,
      readOnly: isReadOnly,
      children: /*#__PURE__*/_jsxs(StyledUITruncateString, {
        tooltip: tooltip,
        children: [/*#__PURE__*/_jsx(SubstringHighlighter, {
          fullText: associationOptionRecord.get('primaryDisplayLabel'),
          textToHighlight: searchText
        }), /*#__PURE__*/_jsx(Small, {
          use: "help",
          className: "m-left-2",
          children: /*#__PURE__*/_jsx(SubstringHighlighter, {
            fullText: associationOptionRecord.get('secondaryDisplayLabel') || '',
            textToHighlight: searchText
          })
        })]
      })
    });
  }, [associationOptionRecord, handleToggleSelectAssociation, isReadOnly, objectId, searchText]);
  if (isReadOnly) {
    return /*#__PURE__*/_jsx(UITooltip, {
      placement: "bottom left",
      title: title,
      children: Option
    });
  }
  return Option;
};
export default UniversalAssociationsSelectOption;